import AdminLayout from "../../layouts/AdminLayout";
import useAuthStore from "../../store/authStore";
import ManagerLayout from "../../layouts/ManagerLayout";
import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import RequestLeave from "../shared/RequestLeave";
import apiClient from "../../api/apiClient";
import { getEditTickets } from "../../api/attendanceService";

// ─── Column definitions ────────────────────────────────────────────────────
const COL_7 = "minmax(160px,2fr) minmax(160px,2fr) 110px 110px 120px 130px 110px";
const COL_6 = "minmax(160px,2fr) minmax(160px,2fr) 110px 110px 130px 110px";

// ─── sessionStorage helpers ───────────────────────────────────────────────
// sessionStorage is scoped to the browser tab and is wiped automatically on
// hard-refresh (F5 / Ctrl+R) or when the tab is closed, so filters reset on
// true page reload but survive React Router back-navigation perfectly.
const SS_KEY = "userList_filterState";

function loadFilters() {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveFilters(state) {
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify(state));
  } catch {}
}

function clearFilters() {
  try {
    sessionStorage.removeItem(SS_KEY);
  } catch {}
}

// ─── Default filter values ────────────────────────────────────────────────
const DEFAULTS = {
  selectedRole:      "ALL",
  selectedLocation:  "ALL",
  selectedManagerId: "SELF",
  searchQuery:       "",
};

function UserList() {
  const role = useAuthStore((state) => state.role);
  const loggedInEmployeeId = useAuthStore((state) => state.employeeId);
  const navigate           = useNavigate();

  const Layout = role === "ADMIN" ? AdminLayout : role === "MANAGER" ? ManagerLayout : AdminLayout;

  // ── Restore persisted filters on mount (or fall back to defaults) ─────
  const saved = loadFilters();

  const [selectedUser,         setSelectedUser]         = useState(null);
  const [users,                setUsers]                = useState([]);
  const [managers,             setManagers]             = useState([]);
  const [loading,              setLoading]              = useState(true);
  const [showInactiveModal,    setShowInactiveModal]    = useState(false);

  const [selectedRole,         setSelectedRole]         = useState(saved?.selectedRole      ?? DEFAULTS.selectedRole);
  const [selectedLocation,     setSelectedLocation]     = useState(saved?.selectedLocation  ?? DEFAULTS.selectedLocation);
  const [selectedManagerId,    setSelectedManagerId]    = useState(saved?.selectedManagerId ?? DEFAULTS.selectedManagerId);
  const [searchQuery,          setSearchQuery]          = useState(saved?.searchQuery       ?? DEFAULTS.searchQuery);

  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [managerDropdownOpen,  setManagerDropdownOpen]  = useState(false);

  const locationRef = useRef(null);
  const managerRef  = useRef(null);

  const [attendanceTickets, setAttendanceTickets] = useState({});
  const [leaveTickets,      setLeaveTickets]      = useState({});
  const [remoteTickets,     setRemoteTickets]     = useState({});
  const [wfhTickets,        setWfhTickets]        = useState({});
  const [viewerIsSuperAdmin, setViewerIsSuperAdmin] = useState(false);

  // ── Persist every filter change to sessionStorage ─────────────────────
  useEffect(() => {
    saveFilters({ selectedRole, selectedLocation, selectedManagerId, searchQuery });
  }, [selectedRole, selectedLocation, selectedManagerId, searchQuery]);

  // ── Viewer superadmin check ───────────────────────────────────────────
  useEffect(() => {
    if (role !== "ADMIN" || !loggedInEmployeeId) return;
    apiClient
      .get(`/accounts/api/users/${loggedInEmployeeId}/`)
      .then((res) => setViewerIsSuperAdmin(res.data.user?.is_superuser === true))
      .catch(() => setViewerIsSuperAdmin(false));
  }, [role, loggedInEmployeeId]);

  // ── Close dropdowns on outside click ─────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (locationRef.current && !locationRef.current.contains(e.target)) setLocationDropdownOpen(false);
      if (managerRef.current  && !managerRef.current.contains(e.target))  setManagerDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Fetch managers (manager role) ─────────────────────────────────────
  useEffect(() => {
    if (role !== "MANAGER") return;
    apiClient.get("/accounts/api/managers/")
      .then((res) => setManagers(res.data.managers || []))
      .catch(console.error);
  }, [role]);

  // ── Fetch user list ───────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (role === "MANAGER") {
      params.manager_id = selectedManagerId === "SELF" ? loggedInEmployeeId : selectedManagerId;
    }
    apiClient.get("/accounts/api/users/", { params })
      .then((res) => setUsers(res.data.users || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedManagerId, role, loggedInEmployeeId]);

  // ── Fetch pending tickets in parallel (admin only) ───────────────────
  useEffect(() => {
    if (role !== "ADMIN") return;
    const fetchAllTickets = async () => {
      try {
        const [ticketsRes, remoteRes, wfhRes] = await Promise.all([
          getEditTickets({ status: "PENDING" }),
          apiClient.get("/attendance/api/remote-attendance/"),
          apiClient.get("/attendance/api/wfh-periods/", { params: { status: "PENDING" } }),
        ]);

        // Pending edit tickets
        const tickets = ticketsRes.data.tickets || [];
        const attMap = {}; const leaveMap = {};
        tickets.forEach((t) => {
          const empId = t.employee_id;
          if (t.request_type === "ATTENDANCE")      attMap[empId]   = (attMap[empId]   || 0) + 1;
          else if (t.request_type === "LEAVE") leaveMap[empId] = (leaveMap[empId] || 0) + 1;
        });
        setAttendanceTickets(attMap);
        setLeaveTickets(leaveMap);

        // Remote attendance pending
        const requests = remoteRes.data.remote_requests || [];
        const remoteMap = {};
        requests.forEach((r) => {
          if (r.employee_id && r.remote_approval_status === "PENDING") remoteMap[r.employee_id] = (remoteMap[r.employee_id] || 0) + 1;
        });
        setRemoteTickets(remoteMap);

        // WFH periods pending
        const periods = wfhRes.data.wfh_periods || [];
        const wfhMap  = {};
        periods.forEach((p) => {
          if (p.employee_id) wfhMap[p.employee_id] = (wfhMap[p.employee_id] || 0) + 1;
        });
        setWfhTickets(wfhMap);
      } catch (err) { console.error("Failed to fetch ticket counts:", err); }
    };
    fetchAllTickets();
  }, [role]);



  // ── Derived lists ─────────────────────────────────────────────────────
  const isViewingOtherManager = role === "MANAGER" && selectedManagerId !== "SELF";

  const activeUsers = [...users]
    .filter((u) => u.is_active !== false)
    .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));

  const inactiveUsers = [...users]
    .filter((u) => u.is_active === false)
    .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));

  const allLocations = [
    "ALL",
    ...Array.from(new Set(activeUsers.map((u) => u.location).filter(Boolean))).sort(),
  ];

  const locationFilteredUsers = selectedLocation === "ALL"
    ? activeUsers
    : activeUsers.filter((u) => u.location === selectedLocation);

  const managerCount  = locationFilteredUsers.filter((u) => u.role === "MANAGER").length;
  const employeeCount = locationFilteredUsers.filter((u) => u.role === "EMPLOYEE").length;
  const adminCount    = locationFilteredUsers.filter((u) => u.role === "ADMIN").length;

  const roleFilteredUsers = locationFilteredUsers.filter(
    (u) => selectedRole === "ALL" || u.role === selectedRole
  );

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return roleFilteredUsers;
    return roleFilteredUsers.filter(
      (u) =>
        (u.full_name   || "").toLowerCase().includes(q) ||
        (u.employee_id || "").toLowerCase().includes(q) ||
        (u.email       || "").toLowerCase().includes(q)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, roleFilteredUsers]);

  const selectedManagerLabel =
    selectedManagerId === "SELF"
      ? "My Employees"
      : managers.find((m) => m.employee_id === selectedManagerId)?.full_name || "Select Manager";

  const activateUser = async (employeeId) => {
    try {
      await apiClient.patch(`/accounts/api/users/${employeeId}/activate_user/`);
      setUsers((prev) => prev.map((u) => u.employee_id === employeeId ? { ...u, is_active: true } : u));
    } catch (err) { console.error("Activation failed:", err); }
  };

  // Reset everything and wipe sessionStorage
  const handleResetFilters = () => {
    setSelectedRole(DEFAULTS.selectedRole);
    setSelectedLocation(DEFAULTS.selectedLocation);
    setSelectedManagerId(DEFAULTS.selectedManagerId);
    setSearchQuery(DEFAULTS.searchQuery);
    clearFilters();
  };

  const hasActiveFilters =
    selectedRole      !== DEFAULTS.selectedRole      ||
    selectedLocation  !== DEFAULTS.selectedLocation  ||
    selectedManagerId !== DEFAULTS.selectedManagerId ||
    searchQuery       !== DEFAULTS.searchQuery;

  // Shared column template: both header and data rows always use the same value
  const colTemplate = isViewingOtherManager ? COL_6 : COL_7;

  // ─────────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="p-3 sm:p-4 md:p-6">

        {/* HEADER */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg flex-shrink-0">
              <span className="text-2xl sm:text-3xl">👥</span>
            </div>
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-white">Employee Directory</h1>
              <p className="text-white/60 text-xs sm:text-sm">Manage and view all employee information</p>
            </div>
          </div>
          <div className="flex flex-row sm:flex-col items-center sm:items-end gap-3 sm:gap-0 w-full sm:w-auto">
            <div className="sm:text-right">
              <p className="text-white/60 text-[10px] sm:text-xs uppercase">Total Active</p>
              <p className="text-2xl sm:text-3xl font-bold text-white">{activeUsers.length}</p>
            </div>
            {role === "ADMIN" && (
              <button
                onClick={() => setShowInactiveModal(true)}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs sm:text-sm font-bold whitespace-nowrap"
              >
                View Inactive
              </button>
            )}
          </div>
        </div>

        {/* READ-ONLY BANNER */}
        <AnimatePresence>
          {isViewingOtherManager && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="mb-4 flex items-center gap-3 px-4 sm:px-5 py-3 rounded-xl bg-amber-500/15 border border-amber-400/30 text-amber-300 text-xs sm:text-sm font-semibold"
            >
              <span className="text-base sm:text-lg flex-shrink-0">👁️</span>
              <span>
                Viewing{" "}
                <span className="text-amber-200 font-bold">
                  {managers.find((m) => m.employee_id === selectedManagerId)?.full_name || selectedManagerId}
                </span>
                's employees — read-only
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ADMIN STATS */}
        {role === "ADMIN" && (
          <div className={`grid gap-3 sm:gap-4 mb-4 sm:mb-6 ${viewerIsSuperAdmin && adminCount > 0 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-1 sm:grid-cols-3"}`}>
            <div className="relative col-span-1" ref={locationRef}>
              <button
                onClick={() => setLocationDropdownOpen((prev) => !prev)}
                className="w-full bg-blue-500/20 border border-blue-400/30 backdrop-blur-xl rounded-xl p-3 sm:p-4 flex items-center gap-3 cursor-pointer text-left"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl sm:text-2xl">📍</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white/60 text-[10px] sm:text-xs uppercase font-bold">LOCATION</p>
                  <p className="text-white text-base sm:text-lg font-bold truncate">
                    {selectedLocation === "ALL" ? "All Locations" : selectedLocation}
                  </p>
                </div>
                <span className="text-white/60 text-xs flex-shrink-0">{locationDropdownOpen ? "▲" : "▼"}</span>
              </button>
              {locationDropdownOpen && (
                <div className="absolute z-50 top-full mt-2 left-0 w-full bg-slate-900 border border-white/20 rounded-xl shadow-2xl overflow-hidden max-h-60 overflow-y-auto">
                  {allLocations.map((loc) => (
                    <button key={loc}
                      onClick={() => { setSelectedLocation(loc); setSelectedRole("ALL"); setLocationDropdownOpen(false); }}
                      className={`w-full text-left px-4 py-3 text-sm font-semibold transition hover:bg-white/10 flex items-center gap-2 ${selectedLocation === loc ? "text-blue-400 bg-blue-500/10" : "text-white/80"}`}>
                      <span>{loc === "ALL" ? "📍" : "🏢"}</span>
                      {loc === "ALL" ? "All Locations" : loc}
                      {selectedLocation === loc && <span className="ml-auto text-blue-400">✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {viewerIsSuperAdmin && adminCount > 0 && (
              <StatCard title="ADMINS" count={adminCount} icon="👑" color="amber"
                isActive={selectedRole === "ADMIN"}
                onClick={() => setSelectedRole((p) => p === "ADMIN" ? "ALL" : "ADMIN")} />
            )}
            <StatCard title="MANAGERS" count={managerCount} icon="👔" color="purple"
              isActive={selectedRole === "MANAGER"}
              onClick={() => setSelectedRole((p) => p === "MANAGER" ? "ALL" : "MANAGER")} />
            <StatCard title="EMPLOYEES" count={employeeCount} icon="💼" color="emerald"
              isActive={selectedRole === "EMPLOYEE"}
              onClick={() => setSelectedRole((p) => p === "EMPLOYEE" ? "ALL" : "EMPLOYEE")} />
          </div>
        )}

        {/* MANAGER STATS */}
        {role === "MANAGER" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="relative" ref={managerRef}>
              <button
                onClick={() => setManagerDropdownOpen((p) => !p)}
                className={`w-full backdrop-blur-xl rounded-xl p-3 sm:p-4 flex items-center gap-3 cursor-pointer text-left border transition-all duration-200 ${isViewingOtherManager ? "bg-amber-500/20 border-amber-400/30" : "bg-indigo-500/20 border-indigo-400/30"}`}>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isViewingOtherManager ? "bg-amber-500" : "bg-indigo-500"}`}>
                  <span className="text-xl sm:text-2xl">👔</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white/60 text-[10px] sm:text-xs uppercase font-bold">SELECT MANAGER</p>
                  <p className="text-white text-sm sm:text-base font-bold truncate">{selectedManagerLabel}</p>
                </div>
                <span className="text-white/60 text-xs flex-shrink-0">{managerDropdownOpen ? "▲" : "▼"}</span>
              </button>
              {managerDropdownOpen && (
                <div className="absolute z-50 top-full mt-2 left-0 w-full bg-slate-900 border border-white/20 rounded-xl shadow-2xl overflow-hidden max-h-64 overflow-y-auto">
                  <button onClick={() => { setSelectedManagerId("SELF"); setManagerDropdownOpen(false); }}
                    className={`w-full text-left px-4 py-3 text-sm font-semibold transition hover:bg-white/10 flex items-center gap-2 ${selectedManagerId === "SELF" ? "text-indigo-400 bg-indigo-500/10" : "text-white/80"}`}>
                    <span>🙋</span> My Employees
                    {selectedManagerId === "SELF" && <span className="ml-auto text-indigo-400">✓</span>}
                  </button>
                  {managers.length > 0 && (
                    <div className="px-4 py-1.5 text-[10px] uppercase tracking-widest text-white/30 font-bold border-t border-white/10">
                      Other Managers
                    </div>
                  )}
                  {managers.map((mgr) => (
                    <button key={mgr.employee_id}
                      onClick={() => { setSelectedManagerId(mgr.employee_id); setManagerDropdownOpen(false); }}
                      className={`w-full text-left px-4 py-3 text-sm font-semibold transition hover:bg-white/10 flex items-center gap-2 ${selectedManagerId === mgr.employee_id ? "text-amber-400 bg-amber-500/10" : "text-white/80"}`}>
                      <span>👤</span><span className="flex-1 truncate">{mgr.full_name}</span>
                      <span className="text-white/30 text-xs flex-shrink-0">{mgr.employee_id}</span>
                      {selectedManagerId === mgr.employee_id && <span className="text-amber-400">✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <StatCard title="EMPLOYEES" count={employeeCount} icon="💼" color="emerald"
              isActive={selectedRole === "EMPLOYEE"}
              onClick={() => setSelectedRole((p) => p === "EMPLOYEE" ? "ALL" : "EMPLOYEE")} />
          </div>
        )}

        {/* ── SEARCH BAR + RESET ────────────────────────────────────────── */}
        <div className="mb-3 sm:mb-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-base pointer-events-none select-none">🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, employee ID or email…"
                className="w-full pl-11 pr-10 py-3 sm:py-3.5 rounded-xl bg-white/10 border border-white/20 focus:border-blue-400/60 focus:bg-white/15 text-white placeholder:text-white/35 text-sm font-medium outline-none transition-all duration-200 backdrop-blur-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 text-lg leading-none transition-colors"
                  aria-label="Clear search"
                >✕</button>
              )}
            </div>

            {/* Reset button — only visible when a filter or search is active */}
            <AnimatePresence>
              {hasActiveFilters && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  onClick={handleResetFilters}
                  className="flex-shrink-0 px-3 sm:px-4 py-3 sm:py-3.5 rounded-xl bg-white/10 border border-white/20 hover:bg-white/20 text-white/70 hover:text-white text-xs font-bold transition-all duration-200 whitespace-nowrap"
                  title="Clear all filters"
                >
                  ↺ Reset
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          {/* Active filter pills */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {selectedLocation !== "ALL" && (
                <FilterPill label={`📍 ${selectedLocation}`} onRemove={() => setSelectedLocation("ALL")} />
              )}
              {selectedRole !== "ALL" && (
                <FilterPill label={`🎭 ${selectedRole}`} onRemove={() => setSelectedRole("ALL")} />
              )}
              {selectedManagerId !== "SELF" && (
                <FilterPill
                  label={`👔 ${managers.find((m) => m.employee_id === selectedManagerId)?.full_name || selectedManagerId}`}
                  onRemove={() => setSelectedManagerId("SELF")}
                />
              )}
              {searchQuery && (
                <FilterPill label={`🔍 "${searchQuery}"`} onRemove={() => setSearchQuery("")} />
              )}
            </div>
          )}

          {searchQuery && (
            <p className="mt-1.5 text-white/40 text-xs pl-1">
              {filteredUsers.length === 0
                ? "No results found"
                : `${filteredUsers.length} result${filteredUsers.length !== 1 ? "s" : ""} found`}
            </p>
          )}
        </div>

        {/* USER LIST */}
        {loading ? (
          <div className="p-8 text-center text-white/60">Loading employees...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-white/40 text-sm">
            {searchQuery ? `No employees match "${searchQuery}"` : "No employees found."}
          </div>
        ) : (
          <>
            {/* ── DESKTOP TABLE (lg+) ── */}
            <div className="hidden lg:block bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-white/20 overflow-hidden">

              {/* HEADER — same colTemplate as every data row */}
              <div
                className="bg-slate-800/80 px-4 py-4 items-center text-white/70 font-bold text-xs uppercase tracking-wider"
                style={{ display: "grid", gridTemplateColumns: colTemplate, gap: "12px" }}
              >
                <div className="pl-2">Employee</div>
                <div>Email</div>
                <div className="text-center">Role</div>
                <div className="text-center">Profile</div>
                {!isViewingOtherManager && <div className="text-center">Apply Leave</div>}
                <div className="text-center">Attendance</div>
                <div className="text-center">Leave</div>
              </div>

              {/* ADMIN ROWS */}
              {role === "ADMIN" && filteredUsers.map((user, index) => {
                const attCount      = attendanceTickets[user.employee_id] || 0;
                const remoteCount   = remoteTickets[user.employee_id]     || 0;
                const leaveCount    = leaveTickets[user.employee_id]      || 0;
                const totalAttBadge = attCount + remoteCount;
                const isAdminUser   = user.role === "ADMIN";
                return (
                  <motion.div key={user.employee_id}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.025 }}
                    className="px-4 py-3.5 items-center border-b border-white/5 hover:bg-white/5 transition"
                    style={{ display: "grid", gridTemplateColumns: colTemplate, gap: "12px" }}
                  >
                    <UserNameCell user={user} />
                    <div className="text-white/65 text-sm truncate" title={user.email}>{user.email}</div>
                    <div className="flex justify-center"><RoleBadge role={user.role} isSuperuser={user.is_superuser} /></div>
                    <div className="flex justify-center">
                      <button onClick={() => navigate(`/admin/user-details/${user.employee_id}`, { state: { from: window.location.pathname } })}
                        className="w-full max-w-[100px] py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-bold text-xs transition-colors">Profile</button>
                    </div>
                    <div className="flex justify-center">
                      {isAdminUser ? (
                        <div className="w-full max-w-[100px] py-2 rounded-lg bg-white/5 border border-white/10 text-white/25 font-bold text-xs text-center">N/A</div>
                      ) : (
                        <button onClick={() => setSelectedUser(user)}
                          className="w-full max-w-[100px] py-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-white font-bold text-xs transition-colors">Apply Leave</button>
                      )}
                    </div>
                    <div className="flex justify-center relative">
                      <button onClick={() => navigate(`/admin/attendance-report/${user.employee_id}`, { state: { from: window.location.pathname } })}
                        className="w-full max-w-[110px] py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white font-bold text-xs relative transition-colors">
                        Attendance
                        <div className="absolute -top-2.5 -right-2 flex flex-row-reverse gap-0.5 z-20">
                          {totalAttBadge > 0 && (
                            <span className="min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center border-2 border-slate-900 animate-pulse">{totalAttBadge}</span>
                          )}
                          {wfhTickets[user.employee_id] > 0 && (
                            <span className="min-w-[18px] h-[18px] px-1 bg-violet-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center border-2 border-slate-900 animate-pulse">{wfhTickets[user.employee_id]}</span>
                          )}
                        </div>
                      </button>
                    </div>
                    <div className="flex justify-center relative">
                      <button onClick={() => navigate(`/admin/leave-report/${user.employee_id}`, { state: { from: window.location.pathname } })}
                        className="w-full max-w-[90px] py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-bold text-xs relative transition-colors">
                        Leave
                        {leaveCount > 0 && (
                          <span className="absolute -top-2 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center border-2 border-slate-900 animate-pulse z-10">{leaveCount}</span>
                        )}
                      </button>
                    </div>
                  </motion.div>
                );
              })}

              {/* MANAGER OWN-TEAM ROWS (7 cols) */}
              {role === "MANAGER" && !isViewingOtherManager && filteredUsers.map((user, index) => {
                const attCount      = attendanceTickets[user.employee_id] || 0;
                const remoteCount   = remoteTickets[user.employee_id]     || 0;
                const totalAttBadge = attCount + remoteCount;
                return (
                  <motion.div key={user.employee_id}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.025 }}
                    className="px-4 py-3.5 items-center border-b border-white/5 hover:bg-white/5 transition"
                    style={{ display: "grid", gridTemplateColumns: colTemplate, gap: "12px" }}
                  >
                    <UserNameCell user={user} />
                    <div className="text-white/65 text-sm truncate" title={user.email}>{user.email}</div>
                    <div className="flex justify-center"><RoleBadge role={user.role} isSuperuser={user.is_superuser} /></div>
                    <div className="flex justify-center">
                      <button onClick={() => navigate(`/manager/user-details/${user.employee_id}`, { state: { from: window.location.pathname } })}
                        className="w-full max-w-[100px] py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-bold text-xs transition-colors">Profile</button>
                    </div>
                    <div className="flex justify-center">
                      <button onClick={() => setSelectedUser(user)}
                        className="w-full max-w-[100px] py-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-white font-bold text-xs transition-colors">Apply Leave</button>
                    </div>
                    <div className="flex justify-center relative">
                      <button onClick={() => navigate(`/manager/attendance-report/${user.employee_id}`, { state: { from: window.location.pathname } })}
                        className="w-full max-w-[110px] py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white font-bold text-xs relative transition-colors">
                        Attendance
                        <div className="absolute -top-2.5 -right-2 flex flex-row-reverse gap-0.5 z-20">
                          {totalAttBadge > 0 && (
                            <span className="min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center border-2 border-slate-900 animate-pulse">{totalAttBadge}</span>
                          )}
                          {wfhTickets[user.employee_id] > 0 && (
                            <span className="min-w-[18px] h-[18px] px-1 bg-violet-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center border-2 border-slate-900 animate-pulse">{wfhTickets[user.employee_id]}</span>
                          )}
                        </div>
                      </button>
                    </div>
                    <div className="flex justify-center">
                      <button onClick={() => navigate(`/manager/leave-report/${user.employee_id}`, { state: { from: window.location.pathname } })}
                        className="w-full max-w-[90px] py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-bold text-xs transition-colors">Leave</button>
                    </div>
                  </motion.div>
                );
              })}

              {/* MANAGER READ-ONLY ROWS (6 cols — no Apply Leave) */}
              {role === "MANAGER" && isViewingOtherManager && filteredUsers.map((user, index) => (
                <motion.div key={user.employee_id}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.025 }}
                  className="px-4 py-3.5 items-center border-b border-white/5 hover:bg-amber-500/5 transition"
                  style={{ display: "grid", gridTemplateColumns: colTemplate, gap: "12px" }}
                >
                  <UserNameCell user={user} muted />
                  <div className="text-white/50 text-sm truncate" title={user.email}>{user.email}</div>
                  <div className="flex justify-center"><RoleBadge role={user.role} isSuperuser={user.is_superuser} muted /></div>
                  <div className="flex justify-center">
                    <button onClick={() => navigate(`/manager/user-details/${user.employee_id}`, { state: { from: window.location.pathname, readOnly: true } })}
                      className="w-full max-w-[100px] py-2 rounded-lg bg-blue-500/60 hover:bg-blue-500/80 text-white font-bold text-xs transition-colors">Profile</button>
                  </div>
                  <div className="flex justify-center">
                    <button onClick={() => navigate(`/manager/attendance-report/${user.employee_id}`, { state: { from: window.location.pathname, readOnly: true } })}
                      className="w-full max-w-[110px] py-2 rounded-lg bg-indigo-500/60 hover:bg-indigo-500/80 text-white font-bold text-xs transition-colors">Attendance</button>
                  </div>
                  <div className="flex justify-center">
                    <button onClick={() => navigate(`/manager/leave-report/${user.employee_id}`, { state: { from: window.location.pathname, readOnly: true } })}
                      className="w-full max-w-[90px] py-2 rounded-lg bg-orange-500/60 hover:bg-orange-500/80 text-white font-bold text-xs transition-colors">Leave</button>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* ── MOBILE / TABLET CARDS (< lg) ── */}
            <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filteredUsers.map((user, index) => {
                const attCount      = attendanceTickets[user.employee_id] || 0;
                const remoteCount   = remoteTickets[user.employee_id]     || 0;
                const leaveCount    = leaveTickets[user.employee_id]      || 0;
                const totalAttBadge = attCount + remoteCount;
                const isReadOnly    = isViewingOtherManager;
                const isAdminUser   = user.role === "ADMIN";
                const profilePath   = role === "ADMIN" ? `/admin/user-details/${user.employee_id}` : `/manager/user-details/${user.employee_id}`;
                const attPath       = role === "ADMIN" ? `/admin/attendance-report/${user.employee_id}` : `/manager/attendance-report/${user.employee_id}`;
                const leavePath     = role === "ADMIN" ? `/admin/leave-report/${user.employee_id}` : `/manager/leave-report/${user.employee_id}`;

                return (
                  <motion.div key={user.employee_id}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.025 }}
                    className={`rounded-2xl border p-4 ${isReadOnly ? "bg-white/5 border-white/10" : "bg-white/10 border-white/20"} backdrop-blur-xl shadow-lg`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isAdminUser ? "bg-gradient-to-br from-amber-500 to-orange-600" : isReadOnly ? "bg-gradient-to-br from-slate-500 to-slate-600" : "bg-gradient-to-br from-pink-500 to-purple-500"}`}>
                        <span className="text-white font-bold text-sm">
                          {user.is_superuser ? "👑" : user.full_name?.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-bold text-sm truncate ${isReadOnly ? "text-white/80" : "text-white"}`}>{user.full_name}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white/40 text-xs">{user.employee_id}</p>
                          <RoleBadge role={user.role} isSuperuser={user.is_superuser} muted={isReadOnly} small />
                        </div>
                      </div>
                    </div>
                    <p className="text-white/50 text-xs mb-3 truncate">📧 {user.email}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => navigate(profilePath, { state: { from: window.location.pathname, readOnly: isReadOnly } })}
                        className="py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-bold text-xs">👤 Profile</button>
                      {!isReadOnly && !isAdminUser ? (
                        <button onClick={() => setSelectedUser(user)}
                          className="py-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-white font-bold text-xs">🏖️ Leave</button>
                      ) : (
                        <div className="py-2 rounded-lg bg-white/5 border border-white/10 text-white/25 font-bold text-xs text-center">
                          {isAdminUser ? "—" : "🔒 Leave"}
                        </div>
                      )}
                      <div className="relative">
                        <button onClick={() => navigate(attPath, { state: { from: window.location.pathname, readOnly: isReadOnly } })}
                          className="w-full py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white font-bold text-xs relative">
                          📊 Attendance
                          <div className="absolute -top-2 -right-1.5 flex flex-row-reverse gap-0.5 z-20">
                            {totalAttBadge > 0 && (
                              <span className="min-w-[16px] h-4 px-1 bg-red-500 rounded-full text-white text-[9px] font-bold flex items-center justify-center border border-slate-900 animate-pulse">{totalAttBadge}</span>
                            )}
                            {wfhTickets[user.employee_id] > 0 && (
                              <span className="min-w-[16px] h-4 px-1 bg-violet-500 rounded-full text-white text-[9px] font-bold flex items-center justify-center border border-slate-900 animate-pulse">{wfhTickets[user.employee_id]}</span>
                            )}
                          </div>
                        </button>
                      </div>
                      <div className="relative">
                        <button onClick={() => navigate(leavePath, { state: { from: window.location.pathname, readOnly: isReadOnly } })}
                          className="w-full py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-bold text-xs relative">
                          📋 Leave Rpt
                          {leaveCount > 0 && (
                            <span className="absolute -top-1.5 -right-1 min-w-[16px] h-4 px-1 bg-red-500 rounded-full text-white text-[9px] font-bold flex items-center justify-center border border-slate-900 animate-pulse z-10">{leaveCount}</span>
                          )}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}

        {/* BACK TO DASHBOARD */}
        <div className="mt-6 flex justify-start">
          <motion.button
            whileHover={{ scale: 1.03, boxShadow: "0 2px 10px rgba(255,255,255,0.05)" }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate(role === "ADMIN" ? "/admin/dashboard" : "/manager/dashboard")}
            className="px-4 py-2 rounded-xl text-xs font-semibold bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 border border-white/15 text-white/90 hover:text-white shadow-md transition-all flex items-center gap-1.5"
          >
            <span>←</span> Back to Dashboard
          </motion.button>
        </div>

        {/* INACTIVE MODAL */}
        {showInactiveModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-xl bg-slate-900 rounded-2xl border border-white/20 shadow-2xl p-5 sm:p-6 max-h-[85vh] overflow-y-auto">
              <button onClick={() => setShowInactiveModal(false)} className="absolute top-4 right-4 text-white/60 hover:text-white text-xl">✕</button>
              <h2 className="text-lg sm:text-xl font-bold text-white mb-4">Inactive Employees</h2>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <StatCard title="MANAGERS"  count={inactiveUsers.filter((u) => u.role === "MANAGER").length}  icon="👔" color="purple" />
                <StatCard title="EMPLOYEES" count={inactiveUsers.filter((u) => u.role === "EMPLOYEE").length} icon="💼" color="emerald" />
              </div>
              {inactiveUsers.length === 0 ? (
                <p className="text-white/60 text-sm">No inactive employees</p>
              ) : (
                <div className="space-y-3">
                  {inactiveUsers.map((user) => (
                    <div key={user.employee_id} className="flex items-center justify-between bg-white/5 p-3 rounded-lg gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-semibold text-sm truncate">{user.full_name}</p>
                          {user.is_superuser && <span className="text-amber-400 text-xs">👑</span>}
                        </div>
                        <p className="text-white/50 text-xs">{user.employee_id}</p>
                      </div>
                      <button onClick={() => activateUser(user.employee_id)}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs sm:text-sm rounded-lg font-bold flex-shrink-0">
                        Activate
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* LEAVE MODAL */}
        {selectedUser && (
          <div
            onClick={() => setSelectedUser(null)}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm cursor-pointer"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative w-full sm:max-w-2xl bg-slate-900 rounded-t-3xl sm:rounded-2xl border border-white/20 shadow-2xl p-5 sm:p-6 max-h-[92vh] overflow-y-auto cursor-default"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              <style>{`div::-webkit-scrollbar { display: none; }`}</style>
              <div className="flex justify-center mb-3 sm:hidden">
                <div className="w-10 h-1 rounded-full bg-white/20" />
              </div>
              <RequestLeave user={selectedUser} onClose={() => setSelectedUser(null)} />
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}

/* ── SHARED UI COMPONENTS ─────────────────────────────────────────────────── */

function FilterPill({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-200 text-xs font-semibold">
      {label}
      <button
        onClick={onRemove}
        className="text-blue-300/60 hover:text-blue-100 leading-none transition-colors"
        aria-label="Remove filter"
      >✕</button>
    </span>
  );
}

function UserNameCell({ user, muted = false }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${user.role === "ADMIN" ? "bg-gradient-to-br from-amber-500 to-orange-600" : muted ? "bg-gradient-to-br from-slate-500 to-slate-600" : "bg-gradient-to-br from-pink-500 to-purple-500"}`}>
        <span className="text-white font-bold text-xs">
          {user.is_superuser ? "👑" : user.full_name?.split(" ").map((n) => n[0]).join("").slice(0, 2)}
        </span>
      </div>
      <div className="min-w-0">
        <p className={`font-semibold text-sm truncate ${muted ? "text-white/80" : "text-white"}`}>{user.full_name}</p>
        <p className="text-white/40 text-xs">{user.employee_id}</p>
      </div>
    </div>
  );
}

function StatCard({ title, count, icon, color, onClick, isActive = false }) {
  const colors  = { purple: "bg-purple-500/20 border-purple-400/30", emerald: "bg-emerald-500/20 border-emerald-400/30", red: "bg-red-500/20 border-red-400/30", blue: "bg-blue-500/20 border-blue-400/30", amber: "bg-amber-500/20 border-amber-400/30" };
  const rings   = { purple: "ring-2 ring-purple-400 ring-offset-2 ring-offset-slate-900", emerald: "ring-2 ring-emerald-400 ring-offset-2 ring-offset-slate-900", red: "ring-2 ring-red-400", blue: "ring-2 ring-blue-400", amber: "ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-900" };
  const iconBgs = { purple: "bg-purple-500", emerald: "bg-emerald-500", red: "bg-red-500", blue: "bg-blue-500", amber: "bg-amber-500" };
  return (
    <div onClick={onClick} className={`${colors[color]} ${isActive ? rings[color] : ""} backdrop-blur-xl rounded-xl p-3 sm:p-4 border cursor-pointer transition-all duration-200`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${iconBgs[color]} flex items-center justify-center flex-shrink-0`}>
          <span className="text-xl sm:text-2xl">{icon}</span>
        </div>
        <div>
          <p className="text-white/60 text-[10px] sm:text-xs uppercase font-bold">{title}</p>
          <p className="text-white text-2xl sm:text-3xl font-bold">{count}</p>
        </div>
      </div>
    </div>
  );
}

function RoleBadge({ role, isSuperuser = false, muted = false, small = false }) {
  const sz    = small ? "px-2 py-0.5 text-[9px]" : "px-2.5 py-1 text-xs";
  const label = isSuperuser ? "Super Admin" : role;
  const style = isSuperuser ? "border-amber-400/40 bg-amber-500/15 text-amber-300" : muted ? "border-white/10 bg-white/5 text-white/40" : "border-white/20 bg-white/5 text-white";
  return (
    <span className={`inline-flex items-center justify-center rounded-lg border font-semibold uppercase whitespace-nowrap ${sz} ${style}`}>
      {isSuperuser && <span className="mr-1">👑</span>}
      {label}
    </span>
  );
}

export default UserList;
